import { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner, ErrorDisplay } from "@/components/loading";
import { useSensors, useSensorSources, useParticipants } from "@/api/hooks";
import { downloadExport } from "@/api/client";
import { Download, FileArchive, FileJson, FileSpreadsheet } from "lucide-react";

export default function ExportPage() {
  const [sourceFilter, setSourceFilter] = useState("");
  const [participantFilter, setParticipantFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const sources = useSensorSources();
  const participants = useParticipants();
  const sensors = useSensors({
    source: sourceFilter || undefined,
    user_id: participantFilter || undefined,
    from: fromDate || undefined,
    to: toDate || undefined,
  });

  const filterParams = {
    source: sourceFilter || undefined,
    user_id: participantFilter || undefined,
    from: fromDate || undefined,
    to: toDate || undefined,
  };

  async function handleDownload(format: "csv" | "json" | "zip") {
    await downloadExport({ ...filterParams, format });
  }

  return (
    <div className="p-4 space-y-6 md:p-6">
      <h1 className="text-2xl font-semibold text-[#1E293B]">Export Data</h1>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:flex lg:flex-wrap lg:items-end">
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">
                Source
              </label>
              <Select
                value={sourceFilter}
                onValueChange={(val) =>
                  setSourceFilter(val === "__all__" ? "" : val ?? "")
                }
              >
                <SelectTrigger className="w-full lg:w-48">
                  <SelectValue placeholder="All sources" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All sources</SelectItem>
                  {(sources.data?.data ?? []).map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">
                Participant
              </label>
              <Select
                value={participantFilter}
                onValueChange={(val) =>
                  setParticipantFilter(val === "__all__" ? "" : val ?? "")
                }
              >
                <SelectTrigger className="w-full lg:w-48">
                  <SelectValue placeholder="All participants" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All participants</SelectItem>
                  {(participants.data?.data ?? []).map((p) => (
                    <SelectItem key={p.user_id} value={p.user_id}>
                      {p.username || p.user_id.slice(0, 8)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">
                From
              </label>
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-full lg:w-40"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">
                To
              </label>
              <Input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="w-full lg:w-40"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Preview</CardTitle>
        </CardHeader>
        <CardContent>
          {sensors.isLoading ? (
            <Spinner />
          ) : sensors.isError ? (
            <ErrorDisplay
              message={sensors.error.message}
              onRetry={() => sensors.refetch()}
            />
          ) : (
            <div className="flex items-center gap-2">
              <Download className="size-4 text-muted-foreground" />
              <span className="text-sm">
                <strong>{(sensors.data?.count ?? 0).toLocaleString()}</strong>{" "}
                rows match your filters
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
        <Button
          size="lg"
          onClick={() => handleDownload("zip")}
          disabled={!sensors.data || sensors.data.count === 0}
        >
          <FileArchive className="size-4" />
          Download ZIP (per-source)
        </Button>
        <Button
          variant="outline"
          size="lg"
          onClick={() => handleDownload("csv")}
          disabled={!sensors.data || sensors.data.count === 0}
        >
          <FileSpreadsheet className="size-4" />
          Download CSV
        </Button>
        <Button
          variant="outline"
          size="lg"
          onClick={() => handleDownload("json")}
          disabled={!sensors.data || sensors.data.count === 0}
        >
          <FileJson className="size-4" />
          Download JSON
        </Button>
      </div>
    </div>
  );
}
